import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl, getAuthHeaders } from '../../../config/api.config';
import { useLocation } from 'react-router-dom';
import './UserManagement.css';

const UserManagement = () => {
  const location = useLocation();
  
  // State for users data
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    school: ''
  });
  
  // State for sorting
  const [sorting, setSorting] = useState({
    field: 'createdAt',
    order: 'desc'
  });
  
  // State for schools (for filter dropdown)
  const [schools, setSchools] = useState([]);
  
  // State for selected user and details modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Read query parameters when component mounts
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const roleParam = queryParams.get('role');
    
    if (roleParam) {
      setFilters(prev => ({
        ...prev,
        role: roleParam
      }));
    }
  }, [location.search]);
  
  // Fetch all users data only once when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Apply client-side filtering and sorting when filters or sorting changes
  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, sorting, allUsers, pagination.page, pagination.limit]);
  
  // Fetch schools for filter dropdown
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await axios.get(
          getApiUrl('/api/v1/stem@home/owner/schools'),
          { headers: getAuthHeaders() }
        );
        
        if (response.data.success) {
          // Update to match the actual API response structure - schools are directly in data array
          const schoolsList = response.data.data || [];
          
          // Check if Unknown School is already in the list
          const hasUnknownSchool = schoolsList.some(school => school.name === "Unknown School");
          
          // If not, add it to the list
          if (!hasUnknownSchool) {
            schoolsList.push({
              _id: "unknown-school",
              name: "Unknown School",
              code: "UNKNOWN"
            });
          }
          
          setSchools(schoolsList);
        }
      } catch (err) {
        console.error('Error fetching schools:', err);
      }
    };
    
    fetchSchools();
  }, []);
  
  // Function to fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        getApiUrl('/api/v1/stem@home/owner/users'),
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        // Store all users
        const users = response.data.users || [];
        setAllUsers(users);
        
        // Initially set filtered users to all users
        setFilteredUsers(users);
        
        // Set total count for pagination
        setPagination(prev => ({
          ...prev,
          total: users.length,
          totalPages: Math.ceil(users.length / prev.limit)
        }));
      } else {
        setError(response.data.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to apply filters and sorting client-side
  const applyFiltersAndSort = () => {
    if (!allUsers.length) return;
    
    let result = [...allUsers];
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(user => 
        (user.name && user.name.toLowerCase().includes(searchLower)) || 
        (user.email && user.email.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply role filter
    if (filters.role) {
      result = result.filter(user => 
        user.role && user.role.toLowerCase() === filters.role.toLowerCase()
      );
    }
    
    // Apply school filter
    if (filters.school) {
      const selectedSchool = schools.find(s => s._id === filters.school);
      if (selectedSchool) {
        result = result.filter(user => 
          user.school && user.school.name === selectedSchool.name
        );
      }
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let valueA, valueB;
      
      // Handle nested fields like school.name
      if (sorting.field.includes('.')) {
        const fields = sorting.field.split('.');
        valueA = fields.reduce((obj, field) => obj && obj[field], a);
        valueB = fields.reduce((obj, field) => obj && obj[field], b);
      } else {
        valueA = a[sorting.field];
        valueB = b[sorting.field];
      }
      
      // Handle string comparisons
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase();
      }
      if (typeof valueB === 'string') {
        valueB = valueB.toLowerCase();
      }
      
      // Handle dates
      if (sorting.field === 'createdAt') {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      }
      
      if (valueA < valueB) return sorting.order === 'asc' ? -1 : 1;
      if (valueA > valueB) return sorting.order === 'asc' ? 1 : -1;
      return 0;
    });
    
    // Apply pagination
    const startIndex = (pagination.page - 1) * pagination.limit;
    const paginatedResult = result.slice(startIndex, startIndex + pagination.limit);
    
    // Update filtered users and pagination
    setFilteredUsers(paginatedResult);
    setPagination(prev => ({
      ...prev,
      total: result.length,
      totalPages: Math.ceil(result.length / prev.limit)
    }));
  };
  
  // Function to fetch user stats
  const fetchUserStats = async (userId) => {
    if (!userId) return;
    
    try {
      setLoadingStats(true);
      const response = await axios.get(
        getApiUrl(`/api/v1/stem@home/owner/users/${userId}/stats`),
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        // Directly set the stats data without accessing a nested data object
        setUserStats(response.data.stats || null);
      }
    } catch (err) {
      console.error('Error fetching user stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 1 // Reset to first page when filters change
    }));
  };
  
  // Handle sorting
  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };
  
  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
    fetchUserStats(user.id);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return dateString;
    }
  };
  
  // Format school name - replace "Unknown School" with a dash
  const formatSchoolName = (school) => {
    if (!school || !school.name) return '-';
    return school.name === 'Unknown School' ? '-' : school.name;
  };
  
  // Get appropriate role badge class
  const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
      case 'student': return 'role-student';
      case 'teacher': return 'role-teacher';
      case 'admin': return 'role-admin';
      case 'owner': return 'role-owner';
      default: return '';
    }
  };
  
  // Render loading state
  if (loading && filteredUsers.length === 0) {
    return (
      <div className="user-management-container">
        <div className="loading">Loading users data...</div>
      </div>
    );
  }
  
  // Render error state
  if (error && filteredUsers.length === 0) {
    return (
      <div className="user-management-container">
        <div className="error">{error}</div>
      </div>
    );
  }
  
  return (
    <div className="user-management-container">
      {/* Page header */}
      <div className="page-header">
        <h1>User Management</h1>
      </div>
      
      {/* Filters */}
      <div className="filters-container">
        <div className="search-group">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or email"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
          />
          <button 
            className="btn btn-primary"
            onClick={() => applyFiltersAndSort()}
          >
            Search
          </button>
        </div>
        
        <div className="filter-group">
          <label htmlFor="role-filter">Role</label>
          <select
            id="role-filter"
            className="form-control"
            name="role"
            value={filters.role}
            onChange={handleFilterChange}
          >
            <option value="">All Roles</option>
            <option value="student">Student</option>
            <option value="teacher">Teacher</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="school-filter">School</label>
          <select
            id="school-filter"
            className="form-control"
            name="school"
            value={filters.school}
            onChange={handleFilterChange}
          >
            <option value="">All Schools</option>
            {schools.map(school => (
              <option key={school._id} value={school._id}>
                {school.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Users table */}
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className={sorting.field === 'name' ? `sort-${sorting.order}` : ''}>
                Name
              </th>
              <th onClick={() => handleSort('email')} className={sorting.field === 'email' ? `sort-${sorting.order}` : ''}>
                Email
              </th>
              <th onClick={() => handleSort('school.name')} className={sorting.field === 'school.name' ? `sort-${sorting.order}` : ''}>
                School
              </th>
              <th onClick={() => handleSort('role')} className={sorting.field === 'role' ? `sort-${sorting.order}` : ''}>
                Role
              </th>
              <th onClick={() => handleSort('createdAt')} className={sorting.field === 'createdAt' ? `sort-${sorting.order}` : ''}>
                Created At
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  {loading ? 'Loading users...' : 'No users found'}
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} onClick={() => handleUserSelect(user)}>
                  <td>
                    <span className="user-name">{user.name || 'N/A'}</span>
                  </td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{formatSchoolName(user.school)}</td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                      {user.role || 'N/A'}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <div className="pagination-container">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </button>
          
          <div className="pagination-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} users)
          </div>
          
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </button>
        </div>
      )}
      
      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="user-details-modal" onClick={() => setShowUserDetails(false)}>
          <div className="user-details-content" onClick={e => e.stopPropagation()}>
            <div className="user-details-header">
              <h3>{selectedUser.name}</h3>
              <button className="close-btn" onClick={() => setShowUserDetails(false)}>×</button>
            </div>
            
            <div className="user-details-body">
              {/* Basic Information */}
              <div className="user-info-section">
                <h4>Basic Information</h4>
                <div className="user-info-grid">
                  <div className="info-item">
                    <label>Name</label>
                    <span>{selectedUser.name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <span>{selectedUser.email || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Role</label>
                    <span>
                      <span className={`role-badge ${getRoleBadgeClass(selectedUser.role)}`}>
                        {selectedUser.role || 'N/A'}
                      </span>
                    </span>
                  </div>
                  <div className="info-item">
                    <label>School</label>
                    <span>{formatSchoolName(selectedUser.school)}</span>
                  </div>
                  <div className="info-item">
                    <label>Created At</label>
                    <span>{formatDate(selectedUser.createdAt)}</span>
                  </div>
                </div>
              </div>
              
              {/* User Statistics */}
              <div className="user-info-section">
                <h4>User Statistics</h4>
                
                {loadingStats ? (
                  <div className="stats-loading">Loading statistics...</div>
                ) : !userStats ? (
                  <div className="no-stats">No statistics available</div>
                ) : (
                  <div className="user-info-grid">
                    {/* Admin Statistics */}
                    {selectedUser.role === 'admin' && userStats.adminStats && (
                      <>
                        <div className="info-item">
                          <label>Total Teachers</label>
                          <span>{userStats.adminStats.totalTeachers || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Students</label>
                          <span>{userStats.adminStats.totalStudents || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Classes</label>
                          <span>{userStats.adminStats.totalClasses || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Active Classes</label>
                          <span>{userStats.adminStats.activeClasses || 0}</span>
                        </div>
                      </>
                    )}
                    
                    {/* Teacher Statistics */}
                    {selectedUser.role === 'teacher' && userStats.teacherStats && (
                      <>
                        <div className="info-item">
                          <label>Total Classes</label>
                          <span>{userStats.teacherStats.totalClasses || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Active Classes</label>
                          <span>{userStats.teacherStats.activeClasses || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Students</label>
                          <span>{userStats.teacherStats.totalStudents || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Questions Created</label>
                          <span>{userStats.teacherStats.questionsCreated || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Submissions Evaluated</label>
                          <span>{userStats.teacherStats.submissionsEvaluated || 0}</span>
                        </div>
                      </>
                    )}
                    
                    {/* Student Statistics */}
                    {selectedUser.role === 'student' && userStats.studentStats && (
                      <>
                        <div className="info-item">
                          <label>Enrolled Classes</label>
                          <span>{userStats.studentStats.enrolledClasses || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Submissions</label>
                          <span>{userStats.studentStats.totalSubmissions || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Average Score</label>
                          <span>{userStats.studentStats.averageScore || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Last Submission</label>
                          <span>{formatDate(userStats.studentStats.lastSubmissionDate) || 'N/A'}</span>
                        </div>
                      </>
                    )}
                    
                    {/* Owner Statistics */}
                    {selectedUser.role === 'owner' && userStats.ownerStats && (
                      <>
                        <div className="info-item">
                          <label>Total Schools</label>
                          <span>{userStats.ownerStats.totalSchools || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Admins</label>
                          <span>{userStats.ownerStats.totalAdmins || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Teachers</label>
                          <span>{userStats.ownerStats.totalTeachers || 0}</span>
                        </div>
                        <div className="info-item">
                          <label>Total Students</label>
                          <span>{userStats.ownerStats.totalStudents || 0}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 